import {
  AlertModal,
  ConfirmationModal,
  ErrorModal,
  Flex,
  Text,
  useOpenModal,
} from '@modusign/design-system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useToggle } from 'react-use';

import { useDeferredState, useSafeState, useUrlQuery } from '@hooks';

import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import { recordGTMEvent } from '@app/store/analytics/actions';
import { convertHtmlToPdf } from '@app/store/transport-layer/form/pdf/convert';

import DraftEditor from '../DraftEditor';
import DraftEditorTopbar from '../DraftEditorTopbar';
import PreviewPage from '../PreviewPage';
import DraftEditorSidebar from './DraftEditorSidebar';
import NewDraftSaveConfirmationModal from './NewDraftSaveConfirmationModal';

interface Form {
  id: string;
  content: string;
  fileUrl: string;
  name: string;
  previewImage: string;
  overview: string;
  quote: string;
  source: {
    type: 'MODUSIGN' | 'EXPERT' | 'ORGANIZATION';
    name: string | null;
    description: string | null;
    site: string | null;
  };
  tags: Array<{ id: string; name: string }>;
}

interface DraftEditorPageProps {
  form?: Form;
  initialContent: string;
  initialTitle: string;
  isSubmitting: boolean;
  onDraftCreate: (params: { name: string; content: string }) => void;
  onDraftUpdate?: (params: { name: string; content: string }) => void;
}

export default function DraftEditorPage({
  form,
  initialContent,
  initialTitle,
  isSubmitting,
  onDraftCreate,
  onDraftUpdate,
}: DraftEditorPageProps) {
  const dispatch = useDispatch();
  const openModal = useOpenModal();

  const [name, setName] = useDeferredState(initialTitle);
  const [savedContent, setSavedContent] = useState(initialContent);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);

  const [sidebarTab, setSidebarTab] = useState<'draft' | 'form'>('draft');

  const [isToDraftsModalOpen, toggleToDraftsModal] = useToggle(false);
  const [isResetModalOpen, toggleResetModal] = useToggle(false);
  const [isNoContentPreviewAlertModalOpen, toggleNoContentPreviewAlertModal] = useToggle(false);
  const [isExitAlertModalOpen, toggleExitAlertModal] = useToggle(false);
  const [isSaveAsNewDraftModelOpen, toggleSaveAsNewDraftModel] = useToggle(false);

  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState<Error | null>(null);

  // TODO(form): 바꾸기
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>();

  const [headings, setHeadings] = useSafeState<Array<{ text: string; id: string }>>([]);

  const location = useLocation();
  const query = useUrlQuery();
  const fromForm = query.get('from') === 'form';
  const history = useHistory();

  useEffect(() => {
    const handler = () => {
      setPreviewPdfUrl(null);
    };

    window.addEventListener('popstate', handler);

    return () => window.removeEventListener('popstate', handler);
  }, []);

  const handleExit = useCallback(
    (path?: string) => {
      if (!fromForm || path) dispatch(recordGTMEvent('exitFromDraftEditor')); // form에서 와서 뒤로가기 하는 경우는 제외
      if (path) history.push(path);
      else history.goBack();
    },
    [dispatch, fromForm, history]
  );

  const openEmptyContentAlertModal = () => {
    openModal((props) => (
      <AlertModal {...props} title="서식 저장 안내" variant="warning">
        내용을 입력해주세요.
      </AlertModal>
    ));
  };

  return (
    <>
      <Flex
        display={previewPdfUrl ? 'none' : 'flex'}
        flexDirection="column"
        height="100vh"
        width="100vw"
      >
        <DraftEditorTopbar
          isSubmitting={isSubmitting}
          name={name}
          onDraftCreate={() => {
            const content = editorRef.current.html.get();
            if (!content) {
              openEmptyContentAlertModal();
            } else if (onDraftUpdate) {
              toggleSaveAsNewDraftModel();
            } else {
              onDraftCreate({ name, content });
            }
          }}
          onDraftUpdate={
            onDraftUpdate
              ? () => {
                  const content = editorRef.current.html.get();
                  if (!content) {
                    openEmptyContentAlertModal();
                  } else {
                    onDraftUpdate({ name, content });
                  }
                }
              : undefined
          }
          onExitClick={() => {
            if (editorRef.current?.html.get() === initialContent) {
              handleExit();
            } else {
              toggleExitAlertModal();
            }
          }}
          onTitleChange={setName}
          onToDraftsClick={
            fromForm
              ? () => {
                  if (editorRef.current?.html.get() === initialContent) {
                    handleExit('/drafts');
                  } else {
                    toggleToDraftsModal();
                  }
                }
              : undefined
          }
        />
        <Flex flexGrow={1}>
          <DraftEditorSidebar
            headings={headings}
            originatedFromForm={!!form}
            tab={sidebarTab}
            onPreviewClick={async () => {
              dispatch(
                recordGTMEvent('formReview', {
                  formName: name,
                })
              );

              const html = editorRef.current?.html.get() || '';
              if (!html) {
                toggleNoContentPreviewAlertModal();
                return;
              }

              setIsLoading(true);
              try {
                const blob = await convertHtmlToPdf(html);
                setPreviewPdfUrl(URL.createObjectURL(blob));
                history.push(location.pathname, location.state);
              } catch (e) {
                setError(e as Error);
              } finally {
                setIsLoading(false);
              }
            }}
            onResetClick={toggleResetModal}
            onTabChange={(nextSidebarTab) => {
              if (sidebarTab !== nextSidebarTab) {
                if (nextSidebarTab === 'draft') {
                  // form => draft
                  editorRef.current?.html.set(savedContent);
                  editorRef.current?.edit.on();

                  dispatch(
                    recordGTMEvent('formEditTabClick', {
                      formName: name,
                    })
                  );
                } else if (nextSidebarTab === 'form') {
                  // draft => form
                  setSavedContent(editorRef.current?.html.get() || '');
                  // 모종의 이유로 setTimeout으로 안감싸면 에디터에서 내용 수정 후 첫번째 호출에는 업데이트가 안됩니다.
                  // 현상은 https://github.com/modusign/modusign-client/pull/5398 의 영상을 참고하세요.
                  setTimeout(() => {
                    editorRef.current?.html.set(form?.content);
                    const element: HTMLDivElement = editorRef.current?.$el?.[0];
                    if (element) {
                      const nextHeadings = Array.from(
                        element.querySelectorAll<HTMLHeadingElement>('h1, h2, h3, h4, h5, h6')
                      );

                      setHeadings(
                        nextHeadings.map((heading, index) => {
                          const id = `DraftEditor__heading-${index}`;
                          heading.id = id;
                          return { text: heading.innerText, id };
                        })
                      );
                    }
                  });
                  editorRef.current?.edit.off();

                  dispatch(
                    recordGTMEvent('formOriginalTabClick', {
                      formName: name,
                    })
                  );
                }

                setSidebarTab(nextSidebarTab);
              }
            }}
          />
          <Flex
            bg="navigationBackgroundNeutralSlightlyDark"
            flexGrow={1}
            justifyContent="center"
            py={10}
          >
            <DraftEditor editorRef={editorRef} initContent={savedContent} />
          </Flex>
        </Flex>
        {isLoading && <GlobalLoadingSpinner />}
        <ErrorModal error={error} isOpen title="서식 변환 실패" onClose={() => setError(null)} />
        <ConfirmationModal
          confirmButtonLabel="이동하기"
          isOpen={isToDraftsModalOpen}
          maxWidth="xs"
          title="작성서식함으로 이동"
          onClose={toggleToDraftsModal}
          onConfirm={() => handleExit('/drafts')}
        >
          <Text as="p" textAlign="center">
            작성서식 목록으로 이동할까요?
            <br />
            마지막 저장 이후 변경한 내용은 저장되지 않습니다.
          </Text>
        </ConfirmationModal>
        <ConfirmationModal
          confirmButtonLabel="나가기"
          isOpen={isExitAlertModalOpen}
          maxWidth="xs"
          title="나가기"
          onClose={toggleExitAlertModal}
          onConfirm={() => handleExit()}
        >
          <Text as="p" textAlign="center">
            서식 작성을 그만하시겠어요?
            <br />
            마지막 저장 이후 변경한 내용은 저장되지 않습니다.
          </Text>
        </ConfirmationModal>
        <ConfirmationModal
          confirmButtonLabel="초기화하기"
          isOpen={isResetModalOpen}
          maxWidth="xs"
          title="내용 초기화하기"
          variant="warning"
          onClose={toggleResetModal}
          onConfirm={() => {
            editorRef.current?.html.set(initialContent);
            toggleResetModal();
          }}
        >
          <Text as="p" textAlign="center">
            서식의 내용을 원본 서식으로 되돌릴까요?
            <br />
            편집한 내용은 초기화되며, 복구할 수 없습니다.
          </Text>
        </ConfirmationModal>
        <AlertModal
          isOpen={isNoContentPreviewAlertModalOpen}
          maxWidth="xs"
          title="서식 미리보기 안내"
          variant="warning"
          onClose={toggleNoContentPreviewAlertModal}
        >
          <Text as="p" textAlign="center">
            미리보기할 내용이 없습니다.
            <br />
            내용을 입력해 주세요.
          </Text>
        </AlertModal>
        {isSaveAsNewDraftModelOpen && (
          <NewDraftSaveConfirmationModal
            initialTitle={name}
            isOpen
            onCancel={toggleSaveAsNewDraftModel}
            onConfirm={(title) => {
              onDraftCreate({ name: title, content: editorRef.current?.html.get() || '' });
              toggleSaveAsNewDraftModel();
            }}
          />
        )}
      </Flex>
      {previewPdfUrl && (
        <PreviewPage
          src={previewPdfUrl}
          title="서식 미리보기"
          onExit={() => {
            URL.revokeObjectURL(previewPdfUrl);
            history.goBack();
          }}
        />
      )}
    </>
  );
}
